/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import axios, { type AxiosError, type AxiosInstance } from 'axios';

import { authStore } from '../stores/auth-store.js';

const API_BASE = '/api/v1';

/**
 * Single Axios instance for the whole app. Carries the access token from the
 * MobX auth store, propagates a request id, and funnels 401s into a central
 * sign-out so stale sessions never linger.
 *
 * The refresh token never touches JS — it lives in an httpOnly cookie and the
 * `/auth/refresh` call relies on `withCredentials`.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = authStore.accessToken;
  if (token !== null) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Request-Id'] = crypto.randomUUID();
  return config;
});

let refreshInFlight: Promise<boolean> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config;

    if (status !== 401 || original === undefined || original.url?.includes('/auth/') === true) {
      throw error;
    }

    // Single-flight refresh: concurrent 401s share one refresh attempt.
    refreshInFlight ??= authStore.refresh().finally(() => {
      refreshInFlight = null;
    });

    const refreshed = await refreshInFlight;
    if (!refreshed) {
      authStore.signOut();
      throw error;
    }
    return apiClient.request(original);
  },
);
